import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Deep-link для модалки "Деталі зустрічі": URL стає source of truth.
 *
 * Поведінка:
 * - `?id=123` у URL → автоматично відкривається модалка через `onOpen(123)`.
 * - Виклик `openWithUrl(id)` ставить `?id=...` → useEffect ловить → `onOpen`.
 * - Виклик `closeWithUrl()` забирає `?id` з URL і викликає `onClose`.
 * - Зміна `?id` (наприклад, кнопкою браузера "назад") синхронно перевідкриває потрібну модалку.
 *
 * Дедуплікація: якщо `selectedId === Number(id з URL)` — `onOpen` не повторюється.
 *
 * Підтримує дзеркально клієнтську (`/appointments`) і експертську (`/expert/appointments`) сторінки.
 */
export function useAppointmentDeepLink(
  selectedId: number | null | undefined,
  onOpen: (id: number) => void,
  onClose: () => void,
) {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const raw = searchParams.get('id');
    if (!raw) return;
    const num = Number(raw);
    if (!Number.isFinite(num) || num <= 0) return;
    if (selectedId === num) return; // вже відкрита
    onOpen(num);
    // selectedId і onOpen — нестабільні; залежність лише на searchParams + onOpen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openWithUrl = useCallback((id: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('id', String(id));
      return next;
    });
  }, [setSearchParams]);

  const closeWithUrl = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('id');
      return next;
    });
    onClose();
  }, [onClose, setSearchParams]);

  return { openWithUrl, closeWithUrl };
}
